import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { Alert, Button } from '@bedrock/components';
import { ChevronThinLeft } from '@bedrock/icons-react';
import { PartnershipFlowDetail } from '@purchase/oaflow';
import { isDevOrTest } from '@mpc/common/src/utils/datracker';
import { solarCooperationInvoke } from '@mpc/common/src/services/api';
import { PartnershipRequestInvoker } from '@purchase/supplier';
import type { IOaFlowData } from '@purchase/flow-detail';

/** cooperation.accident.detail 返回的头部展示字段（AccidentDetailDTO 子集） */
interface AccidentOaParamInfo {
  vendorName?: string;
  accidentNo?: string;
}

/**
 * 协同端事故 OA 反馈流程壳页面。
 *
 * 列表「处理」按钮跳转至此：按事故 oaNo 拉起 OA 反馈流程表单。
 *
 * 实现方式（对齐已跑通的入库/信息变更/RFI）：
 * - 复用 PartnershipFlowDetail（与入库 OnBoard、信息变更 EditNew 同一个组件），
 *   引擎加载、getAccessToken、hiddenModules、userId 处理等全部复用其内部已验证逻辑。
 * - 仅通过 props 覆盖 getFlowData：入库默认调 cooperation.audit.oa.paramInfo（查审核流程表，
 *   返回入库 workflowId），事故需调 cooperation.accident.oa.paramInfo（查 accident_activity 表，
 *   返回事故 workflowId 11440006）。两者查不同表、返回不同 workflowId，不可互换。
 *   PartnershipFlowDetail 内部 ...rest 在默认 getFlowData 之后展开，传入的 getFlowData 会覆盖。
 * - getFlowData 的 userId 用 PartnershipRequestInvoker.getOperatorUserId()（= oaUserId =
 *   generateOaUserCode(mail)，与入库完全一致），不用 paramInfo 返回的 user_id（同值但写法对齐入库）。
 */

const AccidentOaFlow: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const { oaNo } = useParams<{ oaNo: string }>();

  // 查看详情（status=5/6）跳同一路由并带 readonly=1：PartnershipFlowDetail readonly=true
  // 隐藏 ACTIONS 提交模块，只读渲染 OA 流程详情；处理（status=4/已驳回）无该参数，可编辑提交。
  const readonly = new URLSearchParams(location.search).get('readonly') === '1';

  // 缓存 accident.detail 返回值，供头部展示事故详情信息。
  // useEffect 中调用 cooperation.accident.detail 获取，与 getFlowData（oa.paramInfo）职责分离。
  const [paramInfo, setParamInfo] = useState<AccidentOaParamInfo | null>(null);

  // 取数：协同端事故 oa-param（cooperation.accident.oa.paramInfo，VP 协同端已实现）。
  // 覆盖 PartnershipFlowDetail 默认的入库取数。userId 用 invoker 的 operator.userId（与入库一致）。
  const getFlowData = useCallback(async (): Promise<IOaFlowData> => {
    const userId = PartnershipRequestInvoker.getInstance().getOperatorUserId();
    const res = await solarCooperationInvoke(
      {
        api: 'cooperation.accident.oa.paramInfo',
        param: JSON.stringify({ oaNo }),
      },
      { camelCase: { exclude: [/isKOLForMCN/] } },
    );
    return { ...(res?.data ?? {}), userId } as IOaFlowData;
  }, [oaNo]);

  // 头部展示数据：通过 cooperation.accident.detail 获取事故详情（与 oa.paramInfo 职责分离）
  useEffect(() => {
    if (!oaNo) return;
    solarCooperationInvoke(
      {
        api: 'cooperation.accident.detail',
        param: JSON.stringify({ oaNo }),
      },
      { camelCase: { exclude: [/isKOLForMCN/] } },
    ).then((res) => {
      if (res?.data) setParamInfo(res.data);
    });
  }, [oaNo]);

  const handleSubmitSuccess = () => {
    if (history.length > 1) {
      history.goBack();
    } else {
      history.replace('/outCompanyManagement/outCompanyInfo');
    }
  };

  if (!oaNo) {
    return (
      <div className="p-16">
        <Alert type="warning" message="缺少事故单号，无法加载 OA 反馈流程。" />
      </div>
    );
  }

  const handleGoBack = () => {
    if (history.length > 1) {
      history.goBack();
    } else {
      history.replace('/outCompanyManagement/outCompanyInfo');
    }
  };

  // 头部数据：由 useEffect 中 cooperation.accident.detail 返回后即可展示
  const headerTitle = paramInfo
    ? `供应商执行事故处理评估-${paramInfo.vendorName || '--'}-${paramInfo.accidentNo}`
    : null;

  return (
    <>
      <div className="bg-white px-4 pt-4 pb-2 box-border">
        {/* 头部：与查看详情页保持一致（px-4 pt-4 pb-2 + text-xl font-semibold 标题），仅保留标题 */}
        <div className="flex items-center">
          <Button type="checked-neutral" size="small" aria-label="返回" className="mr-3 shrink-0" onClick={handleGoBack} icon={<ChevronThinLeft />} />
          <div className="flex-1 min-w-0">
            {headerTitle && (
              <h1 className="text-xl font-semibold text-color-1 m-0 truncate">{headerTitle}</h1>
            )}
          </div>
        </div>
      </div>
      <PartnershipFlowDetail
        env={isDevOrTest() ? 'test' : 'prod'}
        oaNo={oaNo}
        readonly={readonly}
        getFlowData={getFlowData}
        onSubmitSuccess={handleSubmitSuccess}
      />
    </>
  );
};

export default AccidentOaFlow;
